import { useState, useEffect } from 'react';
import { searchContext, userAppContext } from '../../../context/Context';
import { advancedExamples } from '../AdvancedExamples';
import useFetchSuggestions from '../../../hooks/useFetchSuggestions';
import { validateInput } from '../AdvancedSearchHelpers';
import { useLocation } from 'react-router-dom';
import { useEnrichmentState } from '../../../hooks/useEnrichmentState';
import type { PendingAdvancedSearchRedirect } from '../../../store/types';

const NAME_SEARCH_INPUT_LIMIT = 100;
const ID_SEARCH_INPUT_LIMIT = 1000;

const getSearchInputLimit = (selectedFilter: string) =>
  selectedFilter === 'name' ? NAME_SEARCH_INPUT_LIMIT : ID_SEARCH_INPUT_LIMIT;

interface SearchItem {
  diseaseUMLSCUI?: string;
  strID?: string;
  geneID?: string;
  chemID?: string;
  symbol?: string;
  enrichmentSymbol?: string;
  normalizedId?: string;
  [key: string]: any;
}

export const useAdvancedSearch = () => {
  const location = useLocation();
  const { pendingAdvancedSearchRedirect } = useEnrichmentState();
  const initialNavigationState = (location.state || {}) as {
    searchType?: string;
    selectedFilter?: string;
    text?: string;
    autoSearch?: boolean;
  };
  const initialStoreState: Partial<PendingAdvancedSearchRedirect> = pendingAdvancedSearchRedirect || {};
  const initialState = {
    searchType: initialStoreState.searchType || initialNavigationState.searchType,
    selectedFilter: initialStoreState.selectedFilter || initialNavigationState.selectedFilter,
    text: initialStoreState.text || initialNavigationState.text,
    autoSearch: initialStoreState.autoSearch || initialNavigationState.autoSearch,
  };
  const { setSearchItems } = searchContext();
  const { authorization, isAuthenticated, restrictSources } = userAppContext();
  const [searchType, setSearchType] = useState(initialState.searchType || 'DISEASES');
  const [selectedFilter, setSelectedFilter] = useState<string>(initialState.selectedFilter || '');
  const [text, setText] = useState(initialState.text || '');
  const [selectedItems, setSelectedItems] = useState<SearchItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showTextarea, setShowTextarea] = useState(true);
  const [foundItems, setFoundItems] = useState<SearchItem[]>([]);
  const [notFoundItems, setNotFoundItems] = useState<SearchItem[]>([]);
  const [showFoundItems, setShowFoundItems] = useState(false);
  const [exceededLimit, setExceededLimit] = useState(false);
  const [pendingAutoSearch, setPendingAutoSearch] = useState(
    Boolean(initialState.autoSearch && initialState.text),
  );
  const [skipNextReset, setSkipNextReset] = useState(false);
  const [autoSearchTriggeredFromEnrichment, setAutoSearchTriggeredFromEnrichment] = useState(
    Boolean(initialStoreState.autoSearch && initialStoreState.text),
  );

  const { suggestionsData, loading, error, fetchSuggestions } = useFetchSuggestions(
    searchType,
    selectedFilter,
    authorization,
    isAuthenticated,
  );

  useEffect(() => {
    if (skipNextReset) {
      setSkipNextReset(false);
      return;
    }
    resetSearch();
  }, [searchType, selectedFilter, skipNextReset]);

  useEffect(() => {
    if (pendingAdvancedSearchRedirect?.searchType) {
      setSkipNextReset(true);
      setSearchType(pendingAdvancedSearchRedirect.searchType);
    }
    if (pendingAdvancedSearchRedirect?.selectedFilter) {
      setSkipNextReset(true);
      setSelectedFilter(pendingAdvancedSearchRedirect.selectedFilter);
    }
    if (pendingAdvancedSearchRedirect?.text) {
      setText(pendingAdvancedSearchRedirect.text);
    }
    if (pendingAdvancedSearchRedirect?.autoSearch && pendingAdvancedSearchRedirect?.text) {
      setPendingAutoSearch(true);
      setAutoSearchTriggeredFromEnrichment(true);
    }

    if (location.state?.searchType) {
      setSkipNextReset(true);
      setSearchType(location.state.searchType);
    }
    if (location.state?.text) {
      setText(location.state.text);
    }
    if (location.state?.selectedFilter) {
      setSkipNextReset(true);
      setSelectedFilter(location.state.selectedFilter);
    }
    if (location.state?.autoSearch && location.state?.text) {
      setPendingAutoSearch(true);
      setAutoSearchTriggeredFromEnrichment(true);
    }
  }, [location.state, pendingAdvancedSearchRedirect]);

  useEffect(() => {
    if (!pendingAutoSearch || !text.trim()) {
      return;
    }

    const runAutoSearch = async () => {
      const inputs = text
        .split('\n')
        .map(line => line.trim())
        .filter(line => line);

      if (inputs.length === 0) {
        setPendingAutoSearch(false);
        setAutoSearchTriggeredFromEnrichment(false);
        return;
      }

      if (inputs.length > getSearchInputLimit(selectedFilter)) {
        const limit = getSearchInputLimit(selectedFilter);
        setExceededLimit(true);
        setShowTextarea(true);
        setPendingAutoSearch(false);
        setAutoSearchTriggeredFromEnrichment(false);
        alert(`Search limit of ${limit} items exceeded.`);
        return;
      }

      await fetchSuggestions(inputs);
      setShowTextarea(false);
      setShowSuggestions(true);
      setPendingAutoSearch(false);
      setAutoSearchTriggeredFromEnrichment(false);
    };

    runAutoSearch();
  }, [pendingAutoSearch, text, fetchSuggestions, selectedFilter]);

  const resetSearch = () => {
    setSearchItems([]);
    setText('');
    setShowSuggestions(false);
    setShowFoundItems(false);
    setShowTextarea(true);
    setSelectedItems([]);
    setNotFoundItems([]);
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    setShowSuggestions(false);
  };

  const handleSearchClick = async () => {
    if (!text.trim()) {
      alert('Please provide input text');
      return;
    }

    const inputs = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line);

    const inputLimit = getSearchInputLimit(selectedFilter);

    if (inputs.length > inputLimit) {
      setExceededLimit(true);
      setShowTextarea(true);
      alert(`Search limit of ${inputLimit} items exceeded.`);
      return;
    } else {
      setExceededLimit(false);
    }

    if (inputs.length) {
      await fetchSuggestions(inputs);
      setShowTextarea(false);
      setShowSuggestions(true);
    } else {
      resetSearch();
    }
  };

  const handleBackClick = () => {
    resetSearch();
  };

  const handleInputValidation = (input: any) => {
    const isValid = validateInput(input, searchType, selectedFilter);
    return isValid;
  };

  const handleCheckboxSelection = (item: SearchItem) => {
    setSelectedItems(prevItems => {
      const itemString = JSON.stringify(item);
      const isSelected = prevItems.some(selected => JSON.stringify(selected) === itemString);

      if (isSelected) {
        return prevItems.filter(selected => JSON.stringify(selected) !== itemString);
      } else {
        return [...prevItems, item];
      }
    });
  };

  const handleSearchSelectedItems = () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one item to search.');
      return;
    }

    setFoundItems([]);
    setShowFoundItems(false);

    const uniqueSelectedItems = selectedItems.filter(
      (item: SearchItem, index: number, self: SearchItem[]) => {
        const itemString = JSON.stringify(item);
        return index === self.findIndex((t: SearchItem) => JSON.stringify(t) === itemString);
      },
    );

    const itemsString = uniqueSelectedItems
      .map((item: SearchItem) => {
        if (!item) return '';
        switch (searchType) {
          case 'DISEASES':
            return item.normalizedId || item.diseaseUMLSCUI || '';
          case 'VARIANTS':
            return item.strID || '';
          case 'GENES':
            return item.geneID || item.normalizedId || '';
          case 'CHEMICALS':
            return item.chemID || item.normalizedId || '';
          default:
            return '';
        }
      })
      .filter(id => id)
      .join('-');

    const tab =
      searchType === 'VARIANTS'
        ? 'VARIANTS'
        : searchType === 'DISEASES'
          ? 'DISEASES'
          : searchType === 'GENES'
            ? 'GENES'
            : searchType === 'CHEMICALS'
              ? 'CHEMICALS'
              : 'GDA';
    const source = restrictSources === 'CURATED' ? 'CURATED' : 'ALL';

    const url = `/search?view=${searchType}&idents=${itemsString}&source=${source}&tab=${tab}`;
    window.location.href = url;

    setFoundItems(uniqueSelectedItems);
    setShowFoundItems(true);
  };

  const handleClearAll = () => {
    setSelectedItems([]);
    setFoundItems([]);
  };

  const clearInputText = () => {
    setText('');
  };

  const showExamples = () => {
    const exampleItems = advancedExamples[searchType]?.[selectedFilter] || [];
    setText(exampleItems.join('\n'));
  };

  const handleSelectAll = () => {
    const seen = new Set<string>();
    const allResults: SearchItem[] = [];

    suggestionsData.forEach((item: any) => {
      if (!item?.found || !Array.isArray(item.results)) return;
      item.results.forEach((result: SearchItem) => {
        if (!result) return;
        const key = JSON.stringify(result);
        if (seen.has(key)) return;
        seen.add(key);
        allResults.push(result);
      });
    });

    setSelectedItems(allResults);
  };

  return {
    // State
    searchType,
    setSearchType,
    selectedFilter,
    setSelectedFilter,
    text,
    selectedItems,
    showSuggestions,
    showTextarea,
    foundItems,
    notFoundItems,
    showFoundItems,
    exceededLimit,
    pendingAutoSearch,
    autoSearchTriggeredFromEnrichment,
    suggestionsData,
    loading,
    error,

    // Handlers
    handleQueryChange,
    handleSearchClick,
    handleBackClick,
    handleInputValidation,
    handleCheckboxSelection,
    handleSearchSelectedItems,
    handleClearAll,
    clearInputText,
    showExamples,
    handleSelectAll,
    resetSearch,
  };
};
